import { useEffect, useState } from 'react';
import {
  getCurrentUser,
  signInWithRedirect,
  signOut,
  signIn,
  signUp,
  confirmSignUp as amplifyConfirmSignUp,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  resendSignUpCode,
  fetchAuthSession
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { isMockAuth, useMockAuth } from '../test-utils/mock-auth';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  userId: string | null;
  email: string | null;
}

function useRealAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    userId: null,
    email: null
  });

  const checkAuth = async () => {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      const claims = session.tokens?.idToken?.payload;
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        userId: user.userId,
        email: (claims?.email as string) ?? null
      });
    } catch {
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        userId: null,
        email: null
      });
    }
  };

  useEffect(() => {
    void checkAuth();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn' || payload.event === 'signedOut') {
        void checkAuth();
      }
    });

    return unsubscribe;
  }, []);

  const signInWithEmail = async (email: string, password: string) => {
    return signIn({ username: email, password });
  };

  const signUpWithEmail = async (name: string, email: string, password: string) => {
    return signUp({
      username: email,
      password,
      options: {
        userAttributes: { email, name },
        autoSignIn: true
      }
    });
  };

  const confirmSignUp = async (email: string, code: string) => {
    return amplifyConfirmSignUp({ username: email, confirmationCode: code });
  };

  const resendCode = async (email: string) => {
    return resendSignUpCode({ username: email });
  };

  const resetPassword = async (email: string) => {
    return amplifyResetPassword({ username: email });
  };

  const confirmResetPassword = async (email: string, code: string, newPassword: string) => {
    return amplifyConfirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword
    });
  };

  const signInWithGoogle = () => {
    void signInWithRedirect({ provider: 'Google' });
  };

  const signInWithGitHub = () => {
    void signInWithRedirect({ provider: { custom: 'GitHub' } });
  };

  return {
    ...authState,
    signIn: signInWithGoogle,
    signOut: async () => { try { await signOut({ global: true }); } catch {} },
    signInWithEmail,
    signUpWithEmail,
    confirmSignUp,
    resendCode,
    resetPassword,
    confirmResetPassword,
    signInWithGoogle,
    signInWithGitHub
  };
}

// Select implementation at module load time (safe for hooks — always the same function)
const useAuthImpl = isMockAuth() ? useMockAuth : useRealAuth;

export function useAuth() {
  return useAuthImpl();
}
